import Axios from 'axios-observable'
import {
  OxomiAttachment,
  OxomiData,
  OxomiGlobal,
  OxomiImageResponse,
  OxomiMetaData,
  OxomiVideo,
} from '@obeta/models/lib/models/Oxomi'
import { filter, map, Observable, of, take } from 'rxjs'
import { waitForField, waitForResolve } from '@obeta/utils/lib/waitFor'
import { AxiosResponse } from 'axios'
import { OxomiImageNew, OxomiProductDataInput, OxomiProductDataResponse } from './types'
import { CollectionsOfDatabase, RxDatabase } from 'rxdb'
import { RxLocalDocumentData } from 'rxdb/dist/types/types'
import { ShopDomains } from '@obeta/utils/lib/domains'

// compatibility with new and old way of passing oxomi token and portal data
export const getCompatibleOxomiTokenAndPortal = ({
  db,
  portal,
  accessToken,
}: {
  db: RxDatabase<CollectionsOfDatabase>
  portal?: string
  accessToken?: string
}) => {
  let obs: Observable<RxLocalDocumentData<OxomiMetaData>>
  if (accessToken && portal) {
    obs = of<RxLocalDocumentData<OxomiMetaData>>({
      data: { token: accessToken, portal: portal },
    } as RxLocalDocumentData<OxomiMetaData>)
  } else {
    obs = db.getLocal$<OxomiMetaData>('oxomi').pipe(
      map((document) =>
        document ? document.toJSON() : ({ data: {} } as RxLocalDocumentData<OxomiMetaData>)
      ),
      filter((doc) => {
        return Boolean(doc.data.token && doc.data.portal)
      }),
      take(1)
    )
  }

  return obs
}

// SAP supplierId adds a leading L, which oxomi does not accept or know, so slice it
export const normalizeSupplierIdForOxomi = (supplierId: string) => {
  return supplierId.startsWith('L') ? supplierId.slice(1) : supplierId
}

// 3D images won't exist if there are no images, as they are generated based on the main image.
async function fetchOxomi3DImagesIfAvailable({
  mainImage,
  oxomiId,
  supplierId,
}: {
  mainImage: OxomiImageResponse['mainImage']
  oxomiId: string
  supplierId: string
}) {
  const oxomiField = await waitForField<OxomiGlobal>({ field: 'oxomi' })
  await waitForResolve({ callback: oxomiField.isInitialized })

  const exists = await window.oxomi?.product3dExists({
    itemNumber: oxomiId,
    supplierNumber: supplierId,
  })

  if (exists) {
    return [
      {
        ...mainImage,
        itemNumber: oxomiId,
        supplierNumber: supplierId,
      },
    ]
  }
}

const adjustImageFieldFormat = (image: OxomiImageNew) => {
  return {
    hd: image.hdImageUrl,
    medium: image.mediumImageUrl,
    preview: image.previewImageUrl,
    type: image.type,
    typeName: image.typeName,
    large: image.hdImageUrl,
    thumb: image.previewImageUrl,
  }
}

const prepareVideosFields = (video: OxomiVideo) => {
  return {
    mediumUrl: video.mediumUrl,
    name: video.name,
    previewUrl: video.previewUrl,
    smallUrl: video.smallUrl,
    thumbUrl: video.thumbUrl,
    type: video.type,
    typeName: video.typeName,
    id: video.id,
  }
}

const prepareOxomiAttachmentsFields = (attachment: OxomiAttachment) => {
  return {
    description: attachment.description,
    downloadUrl: attachment.downloadUrl,
    filename: attachment.filename,
    filesize: attachment.filesize,
    iconUrl: attachment.iconUrl,
    type: attachment.type,
    typeName: attachment.typeName,
    url: attachment.url,
  }
}

// API endpoint for fetching product data
// Docs: https://www.oxomi.com/system/api/product#/portals/api/v2/product/data
const OXOMI_PRODUCT_DATA_URL = 'https://oxomi.com/portals/api/v2/product/data'

export const getOxomiProductData = ({
  supplierNumber,
  itemNumber,
  portal,
  accessToken,
  images,
  videos,
  pages,
  attachments,
}: OxomiProductDataInput): Observable<AxiosResponse<{ products: OxomiProductDataResponse[] }>> => {
  const queries: Record<string, { type: 'json' }> = {}

  if (attachments) queries.attachments = { type: 'json' }
  if (videos) queries.videos = { type: 'json' }
  if (pages) queries.pages = { type: 'json' }
  if (images) queries.images = { type: 'json' }

  return Axios.post(OXOMI_PRODUCT_DATA_URL, {
    accessToken,
    portal,
    user: ShopDomains.Obeta,
    outputMode: 'normal',
    products: [{ itemNumber, supplierNumber, queries }],
  })
}

export const buildOxomiData = async ({
  productData,
  oxomiId,
  supplierId,
  threeDImages,
}: {
  productData: OxomiProductDataResponse['queries']
  threeDImages: boolean
  oxomiId?: string
  supplierId?: string
}): Promise<OxomiData> => {
  const oxomiImages = productData.images?.images?.map(adjustImageFieldFormat) || []

  const oxomi3DImages =
    threeDImages && supplierId && oxomiId && productData.images?.images?.[0]
      ? await fetchOxomi3DImagesIfAvailable({
          mainImage: adjustImageFieldFormat(productData.images.images[0]),
          oxomiId: oxomiId,
          supplierId: normalizeSupplierIdForOxomi(supplierId),
        })
      : []

  return {
    oxomiImages,
    oxomi3DImages: oxomi3DImages || [],
    oxomiAttachments:
      productData.attachments?.attachments?.map(prepareOxomiAttachmentsFields) || [],
    oxomiVideos: productData.videos?.videos?.map(prepareVideosFields) || [],
    oxomiPages: productData.pages?.documents || [],
  }
}
